import type { ToolDefinition, ToolExecutionContext } from '../types';

import {
    DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME,
    isMotionGraphicsSceneRequested,
} from './motion-graphics/motionGraphicsSceneDesign';
import { computeSceneShortIds } from './scene-addressing';
import { resolveInsertSlotAfter } from './scene-group-addressing';
import { validateSceneDesignId } from './scene-design-validation';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { addCodeoSceneOperation } from 'js/src/luminary/html-asset-operations';
import { addSceneWithDesignOperation, saveRawLuminary } from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

interface AddSceneRequest {
    scene_design_id: string;
}

interface AddSceneArgs {
    after_scene_group_id?: string;
    scenes: AddSceneRequest[];
}

interface AddedScene {
    sceneId: string;
    sceneDesignId?: string;
    isMotionGraphics: boolean;
}

export class AddSceneTool extends VideoStateTool<AddSceneArgs> {
    public static readonly toolName = 'add_scene';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: AddSceneTool.toolName,
                description:
                    'Add one or more new scenes to the video, each using the specified scene design. Each new scene is its own scene group.',
                parameters: {
                    type: 'object',
                    properties: {
                        after_scene_group_id: {
                            type: 'string',
                            description:
                                'The stable scene_group_id of the scene group the new scenes should be inserted immediately after. Omit to insert them at the very start of the video.',
                        },
                        scenes: {
                            type: 'array',
                            minItems: 1,
                            items: {
                                type: 'object',
                                properties: {
                                    scene_design_id: {
                                        type: 'string',
                                        description:
                                            'The scene design ID to use for this scene (e.g., "template_id:design_name")',
                                    },
                                },
                                required: ['scene_design_id'],
                            },
                            description:
                                'The scenes to add, in the order they should appear in the video.',
                        },
                    },
                    required: ['scenes'],
                },
            },
        };
    }

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AddSceneArgs>;
                if ((parsed.scenes?.length ?? 0) > 1) {
                    return `Adding ${parsed.scenes!.length} scenes`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Adding scene';
    }

    protected override onEditApplied(args: AddSceneArgs): void {
        // Inserting a scene at the very start (no anchor) changes the first scene shown in the thumbnail.
        if (args.after_scene_group_id == null || args.after_scene_group_id.trim() === '') {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'add_photo_alternate';
    }

    /**
     * The motion-graphics branch: create the scene empty, because the graphic itself is authored
     * separately by the design tool. Returns the new scene's id, or an error message.
     */
    private addMotionGraphicsScene(slot: number): AddedScene | { error: string } {
        const { addCodeoScene } = makeOperationsCallable(
            { addCodeoSceneOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const created = addCodeoScene(slot);
        if (!created) {
            return { error: 'could not create a motion graphics scene.' };
        }

        this.startMotionGraphicsSceneBuilding(created.sceneId);

        return { sceneId: created.sceneId, isMotionGraphics: true };
    }

    /** Add one stock-design scene at `slot`, or report why the design id was refused. */
    private addDesignedScene(slot: number, sceneDesignId: string): AddedScene | { error: string } {
        const validation = validateSceneDesignId(this.luminary, sceneDesignId);
        if (validation.error) {
            // validateSceneDesignId's messages are already prefixed with "Error: ".
            return { error: validation.error.replace(/^Error:\s*/, '') };
        }

        const { addSceneWithDesign } = makeOperationsCallable(
            { addSceneWithDesignOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const { sceneId, sceneDesignId: appliedDesignId } = addSceneWithDesign(slot, sceneDesignId);

        // Mark the new scene as "building" so the build preview blurs it
        // until the content it needs lands (set_text/set_media/image_ask).
        this.startSceneBuilding(sceneId);

        return { sceneId, sceneDesignId: appliedDesignId, isMotionGraphics: false };
    }

    /**
     * Report the batch back to the agent: every added scene by the scene_id it is addressed by
     * afterwards, plus any request that was refused, named so a retry can target just that one.
     */
    private describeOutcome(
        added: AddedScene[],
        failures: string[],
        afterSceneGroupId?: string,
    ): string {
        const shortIds = computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id));
        const shortIdOf = (sceneId: string) => shortIds.get(sceneId) ?? sceneId;

        if (added.length === 0) {
            return `Error adding scene: ${failures.join('; ')}`;
        }

        const placement =
            afterSceneGroupId && afterSceneGroupId.trim() !== ''
                ? `right after scene group ${afterSceneGroupId}`
                : 'at the start of the video';
        const describeScene = (scene: AddedScene) =>
            scene.isMotionGraphics
                ? `scene_id ${shortIdOf(scene.sceneId)} (empty motion graphics scene)`
                : `scene_id ${shortIdOf(scene.sceneId)} (scene design "${scene.sceneDesignId}")`;

        const summary =
            added.length === 1
                ? `Success: Added new scene (${describeScene(added[0])}) ${placement}.`
                : `Success: Added ${added.length} new scenes ${placement}, in this order:\n` +
                  added.map((scene) => `  - ${describeScene(scene)}`).join('\n');

        const motionGraphicsShortIds = added
            .filter((scene) => scene.isMotionGraphics)
            .map((scene) => shortIdOf(scene.sceneId));
        const motionGraphicsFollowUp =
            motionGraphicsShortIds.length > 0
                ? `\n\nThe motion graphics scene(s) (${motionGraphicsShortIds.join(', ')}) show nothing yet — ` +
                  `call ${DESIGN_MOTION_GRAPHICS_SCENE_TOOL_NAME} with each scene_id and a brief to build ` +
                  'the graphic.'
                : '';
        const failureReport =
            failures.length > 0
                ? `\n\nNot added (fix and retry just these): ${failures.join('; ')}`
                : '';

        return `${summary}${motionGraphicsFollowUp}${failureReport}`;
    }

    public async execute(args: AddSceneArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, scenes } = args;
        const { parentActionGroup } = context;

        // Resolve the anchor slot up front (fail-closed on an unknown anchor id).
        const firstSlot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        const added: AddedScene[] = [];
        const failures: string[] = [];

        scenes.forEach(({ scene_design_id }, requestIndex) => {
            // Each scene goes one slot further along, so the batch lands in the order given rather
            // than every scene stacking onto the anchor in reverse.
            const slot = firstSlot + added.length;
            const describeRequest = `scenes[${requestIndex}] ("${scene_design_id}")`;

            try {
                if (!scene_design_id) {
                    failures.push(
                        `${describeRequest}: scene_design_id is required (e.g., "template_id:design_name")`,
                    );
                    return;
                }

                const result = isMotionGraphicsSceneRequested(scene_design_id)
                    ? this.addMotionGraphicsScene(slot)
                    : this.addDesignedScene(slot, scene_design_id);

                if ('error' in result) {
                    failures.push(`${describeRequest}: ${result.error}`);
                    return;
                }
                added.push(result);
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                failures.push(`${describeRequest}: ${errorMessage}`);
            }
        });

        // One checkpoint for the whole batch — the scenes were added together and are undone together.
        if (added.length > 0) {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        }

        return this.describeOutcome(added, failures, after_scene_group_id);
    }
}
